import { useCallback, useEffect, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Search, Trash2, Users, ShieldCheck, Bell } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  fetchAdminContacts, deleteContact, downloadContactsCsv,
  fetchEmailLog, downloadEmailLogCsv,
} from "../lib/api";

const SOURCE_LABEL = {
  founding: "Founding", newsletter: "Newsletter", blog: "Blog",
  signin: "Sign-in", gap_notify: "Gap notify", report: "Report",
  save_check: "Saved check", routine: "Routine",
};

function fmtDate(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return String(v).slice(0, 10); }
}

function SourceTag({ s }) {
  return (
    <span className="inline-block rounded-full bg-stone-100 border border-stone-200 px-2 py-0.5 text-[10px] font-semibold text-stone-600 mr-1 mb-1">
      {SOURCE_LABEL[s] || s}
    </span>
  );
}

export default function AdminContacts() {
  const [tab, setTab] = useState("contacts");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");
  const [downloading, setDownloading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchAdminContacts({ q, source, limit: 500 }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [q, source]);

  useEffect(() => { reload(); }, [reload]);

  const onDelete = async (email) => {
    if (!window.confirm(`Delete ${email}? This can't be undone.`)) return;
    try {
      await deleteContact(email);
      toast.success("Contact deleted");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onExport = async () => {
    setDownloading(true);
    try {
      const blob = await downloadContactsCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "blendsafe-contacts.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const rows = data?.contacts || [];
  const bySource = data?.by_source || [];

  return (
    <main data-testid="admin-contacts-page" className="min-h-screen bg-[#fdf8f3] px-4 sm:px-6 py-10 sm:py-12">
      <div className="mx-auto max-w-5xl">
        <AdminHeader />

        <div className="mt-4 inline-flex rounded-xl border border-stone-200 bg-white p-1">
          <button data-testid="tab-contacts" onClick={() => setTab("contacts")}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${tab === "contacts" ? "bg-stone-900 text-white" : "text-stone-600"}`}>
            <Users className="h-3.5 w-3.5" /> Contacts
          </button>
          <button data-testid="tab-alerts" onClick={() => setTab("alerts")}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium ${tab === "alerts" ? "bg-stone-900 text-white" : "text-stone-600"}`}>
            <Bell className="h-3.5 w-3.5" /> Alerts Log
          </button>
        </div>

        {tab === "alerts" ? <AlertsLog /> : (
        <>
        <header className="mt-5 mb-6 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900 flex items-center gap-2">
              <Users className="h-6 w-6 text-stone-500" /> Contacts
            </h1>
            <p className="mt-1.5 text-sm text-stone-600 max-w-xl">
              Every email we've captured — waitlist, newsletter, blog, sign-in, gap-notify,
              saved checks and result reports — in one place.
            </p>
          </div>
          <Button onClick={onExport} disabled={downloading} data-testid="contacts-export" variant="outline">
            {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            Export CSV
          </Button>
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-5">
          <Stat label="Total" value={data?.total} />
          <Stat label="Showing" value={data?.matched} />
          <Stat label="Unsubscribed" value={data?.unsubscribed} tone="red" />
          <Stat label="Sources" value={bySource.length} />
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="contacts-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or name…"
              className="w-full h-9 pl-9 pr-3 rounded-full border border-stone-300 bg-white text-sm text-stone-700 focus:outline-none"
            />
          </div>
          <select
            data-testid="contacts-source-filter"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            className="h-9 px-3 rounded-full border border-stone-300 bg-white text-sm text-stone-700"
          >
            <option value="">All sources</option>
            {Object.keys(SOURCE_LABEL).map((s) => (
              <option key={s} value={s}>
                {SOURCE_LABEL[s]}
                {(() => { const m = bySource.find((b) => b.source === s); return m ? ` (${m.count})` : ""; })()}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-stone-400">
            <Loader2 className="h-5 w-5 animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div data-testid="contacts-empty" className="rounded-2xl border border-dashed border-stone-200 bg-white p-12 text-center text-sm text-stone-500">
            No contacts match this filter yet.
          </div>
        ) : (
          <div data-testid="contacts-table" className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="text-left font-semibold px-4 py-3">Email</th>
                    <th className="text-left font-semibold px-4 py-3">First name</th>
                    <th className="text-left font-semibold px-4 py-3">Sources</th>
                    <th className="text-left font-semibold px-4 py-3">First seen</th>
                    <th className="text-left font-semibold px-4 py-3">Last seen</th>
                    <th className="text-left font-semibold px-4 py-3">Emails</th>
                    <th className="text-left font-semibold px-4 py-3">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((c) => (
                    <tr key={c.email} data-testid={`contact-row-${c.email}`} className="align-top hover:bg-stone-50/60">
                      <td className="px-4 py-3 font-medium text-stone-900 whitespace-nowrap">{c.email}</td>
                      <td className="px-4 py-3 text-stone-700">{c.first_name || "—"}</td>
                      <td className="px-4 py-3 max-w-[220px]">
                        {(c.sources || []).map((s) => <SourceTag key={s} s={s} />)}
                      </td>
                      <td className="px-4 py-3 text-stone-500 text-xs whitespace-nowrap">{fmtDate(c.first_seen)}</td>
                      <td className="px-4 py-3 text-stone-500 text-xs whitespace-nowrap">{fmtDate(c.last_seen)}</td>
                      <td className="px-4 py-3 text-stone-700 tabular-nums">{c.emails_sent}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {c.unsubscribed ? (
                          <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700">Unsubscribed</span>
                        ) : c.verified ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><ShieldCheck className="h-3 w-3" /> Verified</span>
                        ) : (
                          <span className="inline-flex items-center rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[10px] font-semibold text-stone-600">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => onDelete(c.email)}
                          data-testid={`contact-delete-${c.email}`}
                          className="text-stone-400 hover:text-red-600 transition-colors"
                          title="Delete contact"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </main>
  );
}

function fmtDateTime(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return String(v).slice(0, 16); }
}

const TRIGGER_LABEL = {
  verify: "Verify", edit: "Edit", "re-check": "Re-check", digest: "Digest", course: "Course",
};
const STATUS_STYLE = {
  sent: "bg-emerald-50 text-emerald-700 border-emerald-200",
  skipped: "bg-stone-50 text-stone-500 border-stone-200",
  failed: "bg-red-50 text-red-700 border-red-200",
};

function AlertsLog() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [trigger, setTrigger] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [downloading, setDownloading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchEmailLog({ trigger, start, end, limit: 500 });
      setRows(d.items || []);
      setCounts(d.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load alerts log");
    } finally { setLoading(false); }
  }, [trigger, start, end]);

  useEffect(() => { reload(); }, [reload]);

  async function exportCsv() {
    setDownloading(true);
    try {
      const blob = await downloadEmailLogCsv({ trigger, start, end });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "blendsafe-alerts-log.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
    finally { setDownloading(false); }
  }

  return (
    <div data-testid="alerts-log" className="mt-5">
      <header className="mb-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-stone-900 flex items-center gap-2">
            <Bell className="h-6 w-6 text-stone-500" /> Alerts Log
          </h1>
          <p className="mt-1.5 text-sm text-stone-600 max-w-xl">
            Every automated email we attempted — change alerts, re-checks, course nudges and digests —
            with its delivery status.
          </p>
        </div>
        <Button onClick={exportCsv} disabled={downloading} data-testid="alerts-export" variant="outline">
          {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
          Export CSV
        </Button>
      </header>

      <div className="grid grid-cols-3 gap-2 mb-5 max-w-md">
        <Stat label="Sent" value={counts.sent || 0} />
        <Stat label="Skipped" value={counts.skipped || 0} />
        <Stat label="Failed" value={counts.failed || 0} tone="red" />
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select data-testid="alerts-trigger-filter" value={trigger} onChange={(e) => setTrigger(e.target.value)}
          className="h-9 px-3 rounded-full border border-stone-300 bg-white text-sm text-stone-700">
          <option value="">All triggers</option>
          {Object.keys(TRIGGER_LABEL).map((t) => <option key={t} value={t}>{TRIGGER_LABEL[t]}</option>)}
        </select>
        <input type="date" data-testid="alerts-start" value={start} onChange={(e) => setStart(e.target.value)}
          className="h-9 px-3 rounded-full border border-stone-300 bg-white text-sm text-stone-700" />
        <span className="text-stone-400 text-sm">to</span>
        <input type="date" data-testid="alerts-end" value={end} onChange={(e) => setEnd(e.target.value)}
          className="h-9 px-3 rounded-full border border-stone-300 bg-white text-sm text-stone-700" />
      </div>

      {loading ? (
        <div className="rounded-2xl border border-stone-200 bg-white p-10 text-center text-stone-400">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      ) : rows.length === 0 ? (
        <div data-testid="alerts-empty" className="rounded-2xl border border-dashed border-stone-200 bg-white p-12 text-center text-sm text-stone-500">
          No emails logged for this filter yet.
        </div>
      ) : (
        <div data-testid="alerts-table" className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Date</th>
                  <th className="text-left font-semibold px-4 py-3">Email</th>
                  <th className="text-left font-semibold px-4 py-3">Trigger</th>
                  <th className="text-left font-semibold px-4 py-3">Routine / pair</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-left font-semibold px-4 py-3">Message ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {rows.map((r, i) => (
                  <tr key={i} data-testid={`alert-row-${i}`} className="align-top hover:bg-stone-50/60">
                    <td className="px-4 py-3 text-stone-500 text-xs whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                    <td className="px-4 py-3 text-stone-800 whitespace-nowrap">{r.email || "—"}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block rounded-full bg-stone-100 border border-stone-200 px-2 py-0.5 text-[10px] font-semibold text-stone-600">
                        {TRIGGER_LABEL[r.trigger] || r.trigger}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600 max-w-[220px] truncate">{r.target || "—"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLE[r.status] || STATUS_STYLE.skipped}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-400 text-xs truncate max-w-[160px]">{r.message_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "stone" }) {
  const TONES = { stone: "text-stone-900", red: "text-red-700" };
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-bold">{label}</div>
      <div className={`text-2xl font-bold tabular-nums mt-0.5 ${TONES[tone]}`}>{value ?? 0}</div>
    </div>
  );
}
